import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { legalDocumentsApi, authApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { LegalAgreementImpactConfirmation } from './LegalAgreementImpactConfirmation';
import { LegalConfigurationHealthBanner } from './LegalConfigurationHealthBanner';
import type {
    ActiveRoleAssignmentResponse,
    LegalDocumentMutableStatus,
    LegalDocumentListItem,
} from '@pillar-portal/api-types';
import {
    SYSTEM_DISPLAY_TIME_ZONE,
    formatTimestampForMerchantUi,
    hasInternalAdminPrivileges,
} from '@pillar-portal/api-types';
import { LegalDocumentPurpose, Status } from '@pillar-portal/database/dist/frontend';
import { formatLegalDocumentPurpose } from './legal-document-purpose-format';

export function LegalDocumentsListPage() {
    const log = createComponentLogger('LegalDocumentsListPage');
    const breadcrumbNavigate = useBreadcrumbNavigation();
    const queryClient = useQueryClient();
    const [mutationError, setMutationError] = useState<string | null>(null);

    const { data: activeRoleAssignment } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        staleTime: 30000,
    });

    const canView: boolean = hasInternalAdminPrivileges(
        activeRoleAssignment?.activation_state === 'operational' ? activeRoleAssignment : null
    );
    // Deleted documents are listed only on request, for the Roles that can delete them.
    const [displayDeleted, setDisplayDeleted] = useState<boolean>(false);
    const includeDeleted: boolean = canView && displayDeleted;
    const queryKeyPrefix: ReadonlyArray<string> = useMemo(
        () => ['legalDocuments', `canCreate=${canView}`, `includeDeleted=${includeDeleted}`],
        [canView, includeDeleted]
    );
    const [pendingStatusChange, setPendingStatusChange] = useState<{
        legalDocumentUuid: string;
        status: LegalDocumentMutableStatus;
    } | null>(null);
    const updateStatusMutation = useMutation({
        mutationFn: async (variables: {
            legalDocumentUuid: string;
            status: LegalDocumentMutableStatus;
        }) => {
            return await legalDocumentsApi.updateLegalDocumentStatus(variables.legalDocumentUuid, {
                status: variables.status,
            });
        },
        onSuccess: async () => {
            setMutationError(null);
            setPendingStatusChange(null);
            await queryClient.invalidateQueries({ queryKey: ['legalDocuments'] });
            await queryClient.invalidateQueries({ queryKey: ['legalDocument'] });
            await queryClient.invalidateQueries({ queryKey: ['pendingRequiredLegalDocuments'] });
            await queryClient.invalidateQueries({ queryKey: ['legalConfigurationHealth'] });
        },
        onError: (error: unknown) => {
            setPendingStatusChange(null);
            log.error('Failed to update legal document status', error);
            setMutationError('Unable to update legal document status.');
        },
    });

    return (
        <div className="p-6 max-w-5xl mx-auto">
            {canView ? <LegalConfigurationHealthBanner /> : null}
            <LegalAgreementImpactConfirmation
                request={
                    pendingStatusChange === null
                        ? null
                        : {
                              action: 'change_status',
                              legal_document_uuid: pendingStatusChange.legalDocumentUuid,
                              status: pendingStatusChange.status,
                          }
                }
                title={
                    pendingStatusChange?.status === Status.DISABLED
                        ? 'Disable this document?'
                        : 'Activate this document?'
                }
                confirmLabel={
                    pendingStatusChange?.status === Status.DISABLED ? 'Disable' : 'Activate'
                }
                isConfirming={updateStatusMutation.isPending}
                confirmBlocked={(impact) => impact.leaves_no_platform_default}
                onConfirm={() => {
                    if (pendingStatusChange !== null) {
                        updateStatusMutation.mutate(pendingStatusChange);
                    }
                }}
                onCancel={() => setPendingStatusChange(null)}
            >
                {(impact) =>
                    impact.leaves_no_platform_default ? (
                        <p>Assign another platform default before disabling this document.</p>
                    ) : null
                }
            </LegalAgreementImpactConfirmation>
            {mutationError === null ? null : (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {mutationError}
                </div>
            )}
            <InfiniteScrollList<LegalDocumentListItem, string, ReadonlyArray<string>>
                pageTitle="Legal Documents"
                queryPlaceholder="Search by title or version"
                queryKeyPrefix={queryKeyPrefix}
                pageSize={25}
                fetchPage={async (params) => {
                    const response = await legalDocumentsApi.searchLegalDocumentsPage({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                        ...(includeDeleted ? { include_deleted: true } : {}),
                    });
                    return { items: response.legal_documents, pagination: response.pagination };
                }}
                parseCursorParam={(pageParam: unknown) => {
                    if (pageParam === null || pageParam === undefined) {
                        return undefined;
                    }
                    const parseResult = z.string().safeParse(pageParam);
                    if (!parseResult.success) {
                        throw new Error('Expected string cursor');
                    }
                    return parseResult.data;
                }}
                stateCopy={{
                    loadingLabel: 'Loading legal documents...',
                    emptyLabel: 'No legal documents found.',
                    endOfListLabel: 'End of legal documents.',
                    genericErrorLabel: 'Failed to load legal documents.',
                }}
                controls={
                    canView ? (
                        <div className="flex items-center gap-2">
                            <Label
                                htmlFor="legal-documents-display-deleted"
                                className="cursor-pointer"
                            >
                                Display deleted
                            </Label>
                            <Checkbox
                                id="legal-documents-display-deleted"
                                checked={displayDeleted}
                                onCheckedChange={(checked) => {
                                    setDisplayDeleted(checked === true);
                                }}
                            />
                        </div>
                    ) : undefined
                }
                headerActions={
                    canView ? (
                        <Button
                            onClick={() => {
                                runInBackground(
                                    Promise.resolve(
                                        breadcrumbNavigate({
                                            source: 'panel',
                                            target: {
                                                url: '/legal-documents/create',
                                                title: 'Create Legal Document',
                                            },
                                        })
                                    ),
                                    (err: unknown) => {
                                        log.error('Navigation failed', err);
                                    }
                                );
                            }}
                        >
                            Create
                        </Button>
                    ) : undefined
                }
                renderItems={({ items }) => (
                    <div className="space-y-3">
                        {items.map((doc: LegalDocumentListItem) => {
                            return (
                                <div
                                    key={doc.legal_document_uuid}
                                    role="button"
                                    tabIndex={0}
                                    className="w-full cursor-pointer rounded-lg border bg-card text-left hover:bg-gray-50"
                                    onClick={() => {
                                        runInBackground(
                                            Promise.resolve(
                                                breadcrumbNavigate({
                                                    source: 'panel',
                                                    target: {
                                                        url: `/legal-documents/${doc.legal_document_uuid}`,
                                                        title: 'Legal Document Details',
                                                    },
                                                })
                                            ),
                                            (err: unknown) => {
                                                log.error('Navigation failed', err);
                                            }
                                        );
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key !== 'Enter' && event.key !== ' ') {
                                            return;
                                        }
                                        event.preventDefault();
                                        runInBackground(
                                            Promise.resolve(
                                                breadcrumbNavigate({
                                                    source: 'panel',
                                                    target: {
                                                        url: `/legal-documents/${doc.legal_document_uuid}`,
                                                        title: 'Legal Document Details',
                                                    },
                                                })
                                            ),
                                            (err: unknown) => {
                                                log.error('Navigation failed', err);
                                            }
                                        );
                                    }}
                                >
                                    <div className="border-b p-6 pb-2">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="text-base font-semibold">
                                                {doc.legal_document_title} (
                                                {doc.legal_document_version})
                                            </div>
                                            <StatusBadge status={doc.status} />
                                        </div>
                                    </div>
                                    <div className="p-6 pt-4 text-sm text-gray-600">
                                        <div>
                                            In force:{' '}
                                            {formatTimestampForMerchantUi(
                                                doc.in_force_start,
                                                SYSTEM_DISPLAY_TIME_ZONE
                                            )}{' '}
                                            -{' '}
                                            {doc.in_force_end === null
                                                ? 'No end date'
                                                : formatTimestampForMerchantUi(
                                                      doc.in_force_end,
                                                      SYSTEM_DISPLAY_TIME_ZONE
                                                  )}
                                        </div>
                                        <div>
                                            Purpose:{' '}
                                            {formatLegalDocumentPurpose(doc.legal_document_purpose)}
                                        </div>
                                        <div>Content digest: {doc.content_sha256}</div>
                                        {doc.legal_document_purpose ===
                                        LegalDocumentPurpose.ORGANIZATION_AGREEMENT ? (
                                            <div>
                                                Acceptance validity:{' '}
                                                {doc.acceptance_validity_duration_days === null
                                                    ? 'No expiration'
                                                    : `${doc.acceptance_validity_duration_days} days`}
                                            </div>
                                        ) : null}
                                        {canView ? (
                                            <div className="mt-4 flex gap-2">
                                                {doc.status === Status.ACTIVE ? (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        disabled={updateStatusMutation.isPending}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setMutationError(null);
                                                            setPendingStatusChange({
                                                                legalDocumentUuid:
                                                                    doc.legal_document_uuid,
                                                                status: Status.DISABLED,
                                                            });
                                                        }}
                                                    >
                                                        Disable
                                                    </Button>
                                                ) : null}
                                                {doc.status === Status.PENDING ||
                                                doc.status === Status.DISABLED ? (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        disabled={updateStatusMutation.isPending}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setMutationError(null);
                                                            setPendingStatusChange({
                                                                legalDocumentUuid:
                                                                    doc.legal_document_uuid,
                                                                status: Status.ACTIVE,
                                                            });
                                                        }}
                                                    >
                                                        {doc.status === Status.PENDING
                                                            ? 'Activate'
                                                            : 'Re-enable'}
                                                    </Button>
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            />
        </div>
    );
}
